import React, { useState, useEffect, useCallback, lazy, Suspense, type ReactNode } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import ErrorBoundary from "./common/components/ErrorBoundary";
import { canAccessAdmin, isSuperAdminUser } from "./common/utils/jwt";
import RestaurantAccess from "./common/components/RestaurantAccess";
import ResetPassword from "@/features/auth/pages/ResetPassword";
import { getCurrentUser } from "./services/authService";

// Lazy load route components for code splitting and better performance
const Login = lazy(() => import("./features/auth/pages/Login"));
const Dashboard = lazy(() => import("./features/dashboard/pages/Dashboard"));
const AdminDashboard = lazy(() => import("./features/admin/pages/AdminDashboard"));
const SuperAdminDashboard = lazy(() => import("./features/super-admin/SuperAdminDashboard"));
const Settings = lazy(() => import("./features/admin/pages/Settings"));
const Help = lazy(() => import("./features/admin/pages/Help"));
const SearchTable = lazy(() => import("./features/tables/pages/SearchTable"));
const ManageTables = lazy(() => import("./features/tables/pages/ManageTables"));
const ManageStaff = lazy(() => import("./features/staff/pages/ManageStaff"));
const ManageReservations = lazy(() => import("./features/reservations/pages/ManageReservations"));

// Loading component displayed while lazy components are loading
const RouteLoader = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    fontSize: '1.2rem',
    color: '#666'
  }}>
    Loading...
  </div>
);

type RouteWrapperProps = {
  children: ReactNode;
};

const LoginPage = Login as unknown as React.ComponentType<{
  onAuthenticated?: () => void;
}>;

const DashboardPage = Dashboard as unknown as React.ComponentType<{
  isEmployeeView?: boolean;
}>;

const SearchTablePage = SearchTable as unknown as React.ComponentType<{
  isClientView?: boolean;
}>;

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authReady, setAuthReady] = useState(false);

  useEffect(() => {
    const bootstrapSession = async () => {
      try {
        await getCurrentUser();
        setIsAuthenticated(true);
      } catch {
        setIsAuthenticated(false);
      } finally {
        setAuthReady(true);
      }
    };

    bootstrapSession();
  }, []);

  useEffect(() => {
    const handleAuthStateChange = (event: Event) => {
      const customEvent = event as CustomEvent<{ authenticated?: boolean }>;
      setIsAuthenticated(Boolean(customEvent.detail?.authenticated));
    };

    window.addEventListener("auth-state-changed", handleAuthStateChange);
    return () => {
      window.removeEventListener("auth-state-changed", handleAuthStateChange);
    };
  }, []);

  const handleAuthenticated = useCallback(() => {
    setIsAuthenticated(true);
  }, []);

  const ProtectedRoute = useCallback(
    ({ children }: RouteWrapperProps) => {
      if (!authReady) {
        return <RouteLoader />;
      }
      if (!isAuthenticated) {
        return <Navigate to="/" replace />;
      }
      return isSuperAdminUser() ? <RestaurantAccess>{children}</RestaurantAccess> : children;
    },
    [authReady, isAuthenticated]
  );

  const AdminRoute = useCallback(
    ({ children }: RouteWrapperProps) => {
      if (!authReady) {
        return <RouteLoader />;
      }
      if (!isAuthenticated) {
        return <Navigate to="/" replace />;
      }
      if (!canAccessAdmin()) {
        return <Navigate to="/employee-dashboard" replace />;
      }
      return isSuperAdminUser() ? <RestaurantAccess>{children}</RestaurantAccess> : children;
    },
    [authReady, isAuthenticated]
  );

  return (
    <ErrorBoundary>
        <Suspense fallback={<RouteLoader />}>
          <div className="app">
        <Routes>
          <Route path="/super-admin/*" element={
            !authReady ? <RouteLoader /> : !isAuthenticated ? <Navigate to="/" replace /> :
              isSuperAdminUser() ? <SuperAdminDashboard /> : <Navigate to="/admin" replace />
          } />
          <Route path="/" element={<LoginPage onAuthenticated={handleAuthenticated} />} />

          <Route path="/reset-password" element={<ResetPassword />} />

          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                {canAccessAdmin() ? <DashboardPage /> : <Navigate to="/employee-dashboard" replace />}
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin"
            element={
              <AdminRoute>
                <AdminDashboard />
              </AdminRoute>
            }
          />

          <Route
            path="/settings"
            element={
              <AdminRoute>
                <Settings />
              </AdminRoute>
            }
          />

          <Route
            path="/help"
            element={
              <AdminRoute>
                <Help />
              </AdminRoute>
            }
          />

          <Route
            path="/search-table"
            element={
              <ProtectedRoute>
                <SearchTablePage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/manage-tables"
            element={
              <AdminRoute>
                <ManageTables />
              </AdminRoute>
            }
          />

          <Route
            path="/manage-staff"
            element={
              <AdminRoute>
                <ManageStaff />
              </AdminRoute>
            }
          />

          <Route
            path="/manage-reservations"
            element={
              <ProtectedRoute>
                <ManageReservations />
              </ProtectedRoute>
            }
          />

          <Route
            path="/employee-dashboard"
            element={
              <ProtectedRoute>
                <DashboardPage isEmployeeView={true} />
              </ProtectedRoute>
            }
          />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
          </div>
        </Suspense>
    </ErrorBoundary>
  );
}

export default App;
